import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { notificationsApi } from 'src/api/notifications/notifications';

interface NotificationInboxContextValue {
  unreadCount: number;
  refreshUnreadCount: () => Promise<void>;
  setUnreadCount: (count: number) => void;
  markLocallyRead: () => void;
}

const NotificationInboxContext = createContext<NotificationInboxContextValue | null>(null);

export function NotificationInboxProvider({ children }: { children: ReactNode }) {
  const [unreadCount, setUnreadCountState] = useState(0);

  const setUnreadCount = useCallback((count: number) => {
    setUnreadCountState(Math.max(0, Math.floor(count)));
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    try {
      setUnreadCount(await notificationsApi.getUnreadCount());
    } catch (error) {
      console.error('Failed to refresh notification unread count:', error);
    }
  }, [setUnreadCount]);

  const markLocallyRead = useCallback(() => {
    setUnreadCountState((count) => Math.max(0, count - 1));
  }, []);

  useEffect(() => {
    void refreshUnreadCount();
    const interval = window.setInterval(() => void refreshUnreadCount(), 60_000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshUnreadCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshUnreadCount]);

  const value = useMemo(
    () => ({ unreadCount, refreshUnreadCount, setUnreadCount, markLocallyRead }),
    [markLocallyRead, refreshUnreadCount, setUnreadCount, unreadCount]
  );

  return <NotificationInboxContext.Provider value={value}>{children}</NotificationInboxContext.Provider>;
}

export function useNotificationInboxContext(): NotificationInboxContextValue {
  const context = useContext(NotificationInboxContext);
  if (!context) {
    throw new Error('useNotificationInboxContext must be used inside NotificationInboxProvider.');
  }
  return context;
}

export function useOptionalNotificationInboxContext(): NotificationInboxContextValue | null {
  return useContext(NotificationInboxContext);
}
